/**
 * useFilterSync Hook
 *
 * Provides bidirectional URL/state synchronization for financial dashboard filters.
 * Centralizes all filter state management to ensure:
 * - URL is the single source of truth
 * - No flicker on initial render
 * - Browser back/forward navigation works correctly
 * - Shareable URLs with all filter state encoded
 *
 * @see docs/stories/DRE-CLIENT-V2-story-3.004.md
 */

import type {
  ChartType,
  DrillDownGranularity,
} from '@/app/financial/dre/drill-down/types';
import type { ComparisonMode } from '@/app/financial/dre/components';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';

// ============================================================================
// CONSTANTS
// ============================================================================

/** Maximum URL length for shareability (most browsers support up to 2048) */
export const MAX_URL_LENGTH = 2000;

/** URL parameter prefix for namespacing */
const PARAM_PREFIX = 'f_';

/** URL parameter keys (namespaced) */
export const URL_PARAM_KEYS = {
  startDate: `${PARAM_PREFIX}start`,
  endDate: `${PARAM_PREFIX}end`,
  granularity: `${PARAM_PREFIX}gran`,
  comparisonMode: `${PARAM_PREFIX}comp`,
  compareStartDate: `${PARAM_PREFIX}compStart`,
  compareEndDate: `${PARAM_PREFIX}compEnd`,
  search: `${PARAM_PREFIX}search`,
  sortBy: `${PARAM_PREFIX}sortBy`,
  sortOrder: `${PARAM_PREFIX}sortOrder`,
  selectedDimensions: `${PARAM_PREFIX}dims`,
  chartType: `${PARAM_PREFIX}chartType`,
  // SmartFilter dynamic keys use f_filter_* pattern
} as const;

/** Valid granularity values */
export const VALID_GRANULARITIES: DrillDownGranularity[] = [
  'accumulated',
  'yearly',
  'quarterly',
  'monthly',
];

/** Valid chart types */
export const VALID_CHART_TYPES: ChartType[] = ['bar', 'line'];

/** Valid comparison modes */
export const VALID_COMPARISON_MODES: ComparisonMode[] = [
  'none',
  'previous_period',
  'same_period_last_year',
];

/** Valid sort orders */
export const VALID_SORT_ORDERS = ['asc', 'desc'] as const;
export type SortOrder = (typeof VALID_SORT_ORDERS)[number];

// ============================================================================
// TYPES
// ============================================================================

/**
 * Complete filter state for financial dashboards
 */
export interface FilterState {
  // Date range
  startDate: string;
  endDate: string;

  // Time aggregation
  granularity: DrillDownGranularity;

  // Comparison
  comparisonMode: ComparisonMode;
  compareStartDate?: string;
  compareEndDate?: string;

  // Search and sort (Client Matrix)
  search?: string;
  sortBy?: string;
  sortOrder?: SortOrder;

  // Drill-down specific
  selectedDimensions: string[];
  chartType: ChartType;

  // SmartFilter filters (dynamic, keyed by dimension name)
  smartFilters: Record<string, string[]>;
}

/**
 * Default values for filter state
 */
export interface FilterStateDefaults {
  startDate?: string;
  endDate?: string;
  granularity?: DrillDownGranularity;
  comparisonMode?: ComparisonMode;
  chartType?: ChartType;
  selectedDimensions?: string[];
}

/**
 * Options for useFilterSync hook
 */
export interface UseFilterSyncOptions {
  /** Default values for filter state when URL params are missing */
  defaults?: FilterStateDefaults;
  /** Route path for navigation (defaults to current route) */
  routePath?: string;
  /** Route params (e.g., { lineItem: 'ReceitaBruta' }) */
  routeParams?: Record<string, string>;
}

/**
 * Return type for useFilterSync hook
 */
export interface UseFilterSyncReturn {
  /** Current filter state (derived from URL) */
  state: FilterState;

  /** Set a single filter value */
  setFilter: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void;

  /** Set multiple filter values at once */
  setFilters: (updates: Partial<FilterState>) => void;

  /** Clear all filters to defaults */
  clearFilters: () => void;

  /** Copy current URL to clipboard */
  copyLinkToClipboard: () => Promise<boolean>;

  /** Check if URL is safe length */
  isUrlSafeLength: boolean;
}

// ============================================================================
// URL ENCODING/DECODING UTILITIES
// ============================================================================

/**
 * Encode an array to a comma-separated string for URL
 */
export function encodeArrayParam(arr: string[]): string {
  if (!arr || arr.length === 0) return '';
  return arr.join(',');
}

/**
 * Decode a comma-separated string from URL to array
 */
export function decodeArrayParam(value: string | undefined | null): string[] {
  if (!value || typeof value !== 'string') return [];
  return value.split(',').filter(Boolean);
}

/**
 * Parse smart filter params from URL search object
 * Smart filters use pattern: f_filter_{DimensionName}=value1,value2
 */
export function parseSmartFilters(
  search: Record<string, unknown>
): Record<string, string[]> {
  const filters: Record<string, string[]> = {};
  const filterPrefix = `${PARAM_PREFIX}filter_`;

  Object.entries(search).forEach(([key, value]) => {
    if (key.startsWith(filterPrefix) && typeof value === 'string') {
      const dimensionKey = key.slice(filterPrefix.length);
      filters[dimensionKey] = decodeArrayParam(value);
    }
  });

  return filters;
}

/**
 * Serialize smart filters to URL search params
 */
export function serializeSmartFilters(
  filters: Record<string, string[]>
): Record<string, string> {
  const params: Record<string, string> = {};
  const filterPrefix = `${PARAM_PREFIX}filter_`;

  Object.entries(filters).forEach(([key, values]) => {
    if (values && values.length > 0) {
      params[`${filterPrefix}${key}`] = encodeArrayParam(values);
    }
  });

  return params;
}

/**
 * Parse URL search params to FilterState
 */
export function parseUrlToState(
  search: Record<string, unknown>,
  defaults: FilterStateDefaults = {}
): FilterState {
  // Parse granularity with validation
  const rawGranularity = search[URL_PARAM_KEYS.granularity] as string | undefined;
  const granularity = VALID_GRANULARITIES.includes(rawGranularity as DrillDownGranularity)
    ? (rawGranularity as DrillDownGranularity)
    : defaults.granularity || 'monthly';

  // Parse comparison mode with validation
  const rawComparisonMode = search[URL_PARAM_KEYS.comparisonMode] as string | undefined;
  const comparisonMode = VALID_COMPARISON_MODES.includes(rawComparisonMode as ComparisonMode)
    ? (rawComparisonMode as ComparisonMode)
    : defaults.comparisonMode || 'none';

  // Parse chart type with validation
  const rawChartType = search[URL_PARAM_KEYS.chartType] as string | undefined;
  const chartType = VALID_CHART_TYPES.includes(rawChartType as ChartType)
    ? (rawChartType as ChartType)
    : defaults.chartType || 'bar';

  // Parse sort order with validation
  const rawSortOrder = search[URL_PARAM_KEYS.sortOrder] as string | undefined;
  const sortOrder = VALID_SORT_ORDERS.includes(rawSortOrder as SortOrder)
    ? (rawSortOrder as SortOrder)
    : undefined;

  // Parse selected dimensions (can be array or comma-separated string)
  let selectedDimensions: string[];
  const rawDims = search[URL_PARAM_KEYS.selectedDimensions];
  if (Array.isArray(rawDims)) {
    selectedDimensions = rawDims.filter((d): d is string => typeof d === 'string');
  } else if (typeof rawDims === 'string') {
    selectedDimensions = decodeArrayParam(rawDims);
  } else {
    selectedDimensions = defaults.selectedDimensions || [];
  }

  return {
    startDate: (search[URL_PARAM_KEYS.startDate] as string) || defaults.startDate || '',
    endDate: (search[URL_PARAM_KEYS.endDate] as string) || defaults.endDate || '',
    granularity,
    comparisonMode,
    compareStartDate: (search[URL_PARAM_KEYS.compareStartDate] as string) || undefined,
    compareEndDate: (search[URL_PARAM_KEYS.compareEndDate] as string) || undefined,
    search: (search[URL_PARAM_KEYS.search] as string) || undefined,
    sortBy: (search[URL_PARAM_KEYS.sortBy] as string) || undefined,
    sortOrder,
    selectedDimensions,
    chartType,
    smartFilters: parseSmartFilters(search),
  };
}

/**
 * Serialize FilterState to URL search params
 */
export function stateToUrl(
  state: FilterState,
  defaults: FilterStateDefaults = {}
): Record<string, string | undefined> {
  const params: Record<string, string | undefined> = {};

  // Only include non-default values to keep URL clean
  if (state.startDate) {
    params[URL_PARAM_KEYS.startDate] = state.startDate;
  }
  if (state.endDate) {
    params[URL_PARAM_KEYS.endDate] = state.endDate;
  }
  if (state.granularity && state.granularity !== defaults.granularity) {
    params[URL_PARAM_KEYS.granularity] = state.granularity;
  }
  if (state.comparisonMode && state.comparisonMode !== 'none') {
    params[URL_PARAM_KEYS.comparisonMode] = state.comparisonMode;
  }
  if (state.compareStartDate) {
    params[URL_PARAM_KEYS.compareStartDate] = state.compareStartDate;
  }
  if (state.compareEndDate) {
    params[URL_PARAM_KEYS.compareEndDate] = state.compareEndDate;
  }
  if (state.search) {
    params[URL_PARAM_KEYS.search] = state.search;
  }
  if (state.sortBy) {
    params[URL_PARAM_KEYS.sortBy] = state.sortBy;
  }
  if (state.sortOrder) {
    params[URL_PARAM_KEYS.sortOrder] = state.sortOrder;
  }
  if (state.selectedDimensions.length > 0) {
    params[URL_PARAM_KEYS.selectedDimensions] = encodeArrayParam(state.selectedDimensions);
  }
  if (state.chartType && state.chartType !== defaults.chartType) {
    params[URL_PARAM_KEYS.chartType] = state.chartType;
  }

  // Add smart filters
  const smartFilterParams = serializeSmartFilters(state.smartFilters);
  Object.assign(params, smartFilterParams);

  return params;
}

/**
 * Check if URL is within safe length limits
 */
export function isUrlSafeLength(url: string): boolean {
  return url.length <= MAX_URL_LENGTH;
}

/**
 * Truncate filters to fit within URL length limit
 * Priority: dates > granularity > comparison > chartType > selectedDimensions > smartFilters > search
 */
export function truncateFiltersForUrl(
  state: FilterState,
  defaults: FilterStateDefaults = {}
): FilterState {
  const testUrl = () => {
    const params = stateToUrl(state, defaults);
    const searchString = new URLSearchParams(
      Object.entries(params).filter((e): e is [string, string] => e[1] !== undefined)
    ).toString();
    return `?${searchString}`;
  };

  // If already within limits, return as-is
  if (isUrlSafeLength(testUrl())) {
    return state;
  }

  // Create mutable copy
  const truncated = { ...state };

  // 1. Remove smart filters (lowest priority)
  if (!isUrlSafeLength(testUrl())) {
    truncated.smartFilters = {};
    console.warn('[useFilterSync] URL too long, dropping smart filters');
  }

  // 2. Remove selected dimensions
  if (!isUrlSafeLength(testUrl())) {
    truncated.selectedDimensions = [];
    console.warn('[useFilterSync] URL too long, dropping selected dimensions');
  }

  // 3. Remove search
  if (!isUrlSafeLength(testUrl())) {
    truncated.search = undefined;
    console.warn('[useFilterSync] URL too long, dropping search');
  }

  return truncated;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * useFilterSync Hook
 *
 * Provides bidirectional URL/state synchronization for filter state.
 * URL is the single source of truth - state is derived from URL params.
 *
 * @example
 * ```tsx
 * const { state, setFilter, setFilters, copyLinkToClipboard } = useFilterSync({
 *   defaults: {
 *     granularity: 'monthly',
 *     chartType: 'bar',
 *   },
 * });
 *
 * // Read state
 * console.log(state.startDate, state.granularity);
 *
 * // Update single filter
 * setFilter('granularity', 'quarterly');
 *
 * // Update multiple filters
 * setFilters({ startDate: '2024-01-01', endDate: '2024-12-31' });
 *
 * // Copy shareable link
 * await copyLinkToClipboard();
 * ```
 */
export function useFilterSync(options: UseFilterSyncOptions = {}): UseFilterSyncReturn {
  const { defaults = {}, routeParams } = options;

  // Get current URL search params from TanStack Router
  // Using type assertion since we're accessing the generic search params
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();

  // Parse URL to state (memoized to prevent unnecessary re-renders)
  const state = useMemo(
    () => parseUrlToState(search, defaults),
    [search, defaults]
  );

  // Check if current URL is within safe length
  const isUrlSafe = useMemo(() => {
    if (typeof window === 'undefined') return true;
    return isUrlSafeLength(window.location.href);
  }, [search]);

  // Update URL with new filter values
  const updateUrl = useCallback(
    (newState: FilterState) => {
      const params = stateToUrl(newState, defaults);

      // Build new search params, preserving non-filter params
      const newSearch: Record<string, string | undefined> = {};

      // Copy non-filter params from current search
      Object.keys(search).forEach((key) => {
        if (!key.startsWith(PARAM_PREFIX)) {
          const value = search[key];
          if (typeof value === 'string') {
            newSearch[key] = value;
          }
        }
      });

      // Add new filter params
      Object.entries(params).forEach(([key, value]) => {
        newSearch[key] = value;
      });

      // Navigate with replace to avoid history spam
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      void navigate({
        search: newSearch as any,
        params: routeParams as any,
        replace: true,
      });
    },
    [navigate, defaults, routeParams, search]
  );

  // Set a single filter value
  const setFilter = useCallback(
    <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
      const newState = { ...state, [key]: value };
      updateUrl(newState);
    },
    [state, updateUrl]
  );

  // Set multiple filter values at once
  const setFilters = useCallback(
    (updates: Partial<FilterState>) => {
      const newState = { ...state, ...updates };
      updateUrl(newState);
    },
    [state, updateUrl]
  );

  // Clear all filters to defaults
  const clearFilters = useCallback(() => {
    const newState: FilterState = {
      startDate: defaults.startDate || '',
      endDate: defaults.endDate || '',
      granularity: defaults.granularity || 'monthly',
      comparisonMode: defaults.comparisonMode || 'none',
      compareStartDate: undefined,
      compareEndDate: undefined,
      search: undefined,
      sortBy: undefined,
      sortOrder: undefined,
      selectedDimensions: defaults.selectedDimensions || [],
      chartType: defaults.chartType || 'bar',
      smartFilters: {},
    };
    updateUrl(newState);
  }, [defaults, updateUrl]);

  // Copy current URL to clipboard
  const copyLinkToClipboard = useCallback(async (): Promise<boolean> => {
    if (typeof window === 'undefined' || !navigator.clipboard) {
      console.warn('[useFilterSync] Clipboard API not available');
      return false;
    }

    try {
      await navigator.clipboard.writeText(window.location.href);
      return true;
    } catch (error) {
      console.error('[useFilterSync] Failed to copy to clipboard:', error);
      return false;
    }
  }, []);

  return {
    state,
    setFilter,
    setFilters,
    clearFilters,
    copyLinkToClipboard,
    isUrlSafeLength: isUrlSafe,
  };
}

export default useFilterSync;
